/**
 * flyApi.js
 *
 * Boarding passes for Flight YJ030, from PersonalBackend.
 * The backend masks the seat and origin ("•••") for everyone but the guest who claimed the pass,
 * or a browser that checked in online with the PNR and last name (remembered in an HttpOnly cookie).
 */

import { BACKEND_URL, BackendError, callBackend } from "../../../config/backend";
import { auth } from "../../../config/firebase";
import { isTrial } from "./flyPage";

/**
 * The backend path for this run: the trial run's API is the same, under /trial
 */
const apiPath = (path) => (isTrial() ? `/trial${path}` : path);

const call = (path, options) => callBackend(apiPath(path), options);

// 6 uppercase letters/digits
export const PNR_PATTERN = /^[A-Z0-9]{6}$/;

/**
 * A boarding pass, and what the visitor may see of it
 * @param {String} pnr      -   e.g. '6ZGBJS'
 * @returns {Object}        -   { status, yourName, rsvp (owner or checked in only), isAdmin, pass: { pnr, fullName, seat, fromCode, fromCity, colour } }
 */
export const getBoardingPass = (pnr) => call(`/fly/${encodeURIComponent(pnr)}`, { withCookies: true });

/**
 * Claims the pass for the signed-in user, if their saved full name matches the passenger's
 * @param {String} pnr      -   e.g. '6ZGBJS'
 * @returns {Object}        -   Same shape as getBoardingPass; status is 'owner' if it worked
 */
export const claimBoardingPass = (pnr) => call(`/fly/${encodeURIComponent(pnr)}/claim`, { method: "POST" });

/**
 * Saves the signed-in user's full name. Only works once (409 after that).
 * @param {String} fullName -   e.g. 'Yash Jain'
 * @returns {Object}        -   { fullName }
 */
export const saveFullName = (fullName) => call("/me/full-name", { method: "POST", body: { fullName } });

/**
 * The owner says whether they're coming. Can be changed any time.
 * @param {String} pnr          -   e.g. '6ZGBJS'
 * @param {Boolean} attending   -   true: coming, false: not coming
 * @returns {Object}            -   Same shape as getBoardingPass, with the new rsvp
 */
export const rsvpBoardingPass = (pnr, attending) => call(`/fly/${encodeURIComponent(pnr)}/rsvp`, { method: "POST", body: { attending }, withCookies: true });

/**
 * Every guest and their RSVP, for the host (admins only)
 * @returns {Object}            -   { guests: [{ pnr, fullName, rsvp, rsvpAt, claimed }] (coming, not coming, no reply), counts: { coming, notComing, noReply } }
 */
export const getRsvps = () => call("/fly/rsvps");

// ---------------- Online check-in (no account: PNR + last name, remembered in a cookie) ---------------- //

// Online check-in (the boarding pass) is open until then: 30 Oct 2026, 20:00 IST (the backend has the final say)
export const CHECK_IN_CLOSES = new Date("2026-10-30T20:00:00+05:30");

// Customs declarations (and so check-in, as guests see it) close then: 30 Oct 2026, 15:00 IST. The same as the backend's.
export const DECLARATIONS_CLOSE = new Date("2026-10-30T15:00:00+05:30");

/**
 * Checks in with the PNR and last name (or full name), and remembers it in this browser
 * @returns {Object}            -   The booking: { pass, rsvp, checkedInAt, declarations, declarationsSubmittedAt, checkInOpen, checkInClosesAt }
 */
export const checkIn = (pnr, lastName) => call("/fly/check-in", { method: "POST", body: { pnr, lastName }, withCookies: true });

/**
 * The PNRs this browser has checked in with
 * @returns {Object}            -   { pnrs: ['CF38US'] }
 */
export const getCheckInSession = () => call("/fly/check-in/session", { withCookies: true });

/**
 * The booking, if this browser checked in with this PNR (401 otherwise)
 */
export const getCheckedInBooking = (pnr) => call(`/fly/check-in/${encodeURIComponent(pnr)}`, { withCookies: true });

/**
 * Saves the customs declaration, which completes check-in
 * @param {Array} statements    -   Three strings
 * @param {Number} lie          -   0, 1 or 2
 * @returns {Object}            -   The updated booking
 */
export const saveDeclarations = (pnr, statements, lie) => call(`/fly/check-in/${encodeURIComponent(pnr)}/declarations`, { method: "PUT", body: { statements, lie }, withCookies: true });

/**
 * Saves the guest's voice message for the birthday boy (replacing any earlier one)
 * @param {Blob} recording      -   The audio, e.g. from MediaRecorder
 * @returns {Object}            -   The updated booking (voiceMemoAt is set)
 */
export const uploadVoiceMemo = async (pnr, recording) => {
    const form = new FormData();
    const extension = recording.type.includes("mp4") ? "m4a" : recording.type.includes("ogg") ? "ogg" : "webm";
    form.append("memo", recording, `memo.${extension}`);
    const response = await fetch(`${BACKEND_URL}${apiPath(`/fly/check-in/${encodeURIComponent(pnr)}/voice-memo`)}`, { method: "POST", body: form, credentials: "include" });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new BackendError(data.error || `Upload failed (${response.status})`, response.status);
    return data;
};

/**
 * Deletes the guest's voice message
 * @returns {Object}            -   The updated booking
 */
export const deleteVoiceMemo = (pnr) => call(`/fly/check-in/${encodeURIComponent(pnr)}/voice-memo`, { method: "DELETE", withCookies: true });

/**
 * The Apple Wallet pass's link. Opened as a normal link (with the cookie), so Safari offers to add it.
 */
export const applePassUrl = (pnr) => `${BACKEND_URL}${apiPath(`/fly/check-in/${encodeURIComponent(pnr)}/apple-pass`)}`;

/**
 * A link that adds the pass to Google Wallet
 * @returns {Object}            -   { saveUrl }
 */
export const getGooglePass = (pnr) => call(`/fly/check-in/${encodeURIComponent(pnr)}/google-pass`, { withCookies: true });

// Google Wallet only saves this issuer's passes for its test accounts until Google approves it for publishing
// (anyone else sees "this pass is only for testing"). Set to true once the Google Wallet console shows it approved.
export const GOOGLE_WALLET_READY = false;

// ---------------- Souvenirs, after the party (/fly/souvenir): who you are comes from your sign-in ---------------- //

// Admins can preview a guest's souvenirs with ?pnr= (the backend ignores it for everyone else)
const souvenirQuery = (pnr) => (pnr ? `?pnr=${encodeURIComponent(pnr)}` : "");

/**
 * The signed-in guest's boarding pass and passport details
 * @param {String} pnr      -   Admins only: whose to preview
 * @returns {Object}        -   { isAdmin, pass: { pnr, fullName, colour, seat, fromCode, fromCity }, passport: { country, city, hasPhoto, local_name, ... } }
 */
export const getSouvenir = (pnr) => callBackend(`/fly/souvenir${souvenirQuery(pnr)}`);

/**
 * The guest's passport photo, as a data: URL (so it's drawn into the PNG and PDF too), or null if there isn't one
 */
export const getSouvenirPhoto = async (pnr) => {
    const token = await auth.currentUser?.getIdToken();
    const response = await fetch(`${BACKEND_URL}/fly/souvenir/photo${souvenirQuery(pnr)}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (response.status === 404) return null;
    if (!response.ok) throw new BackendError(`Couldn't load your photo (${response.status})`, response.status);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

/**
 * A short-lived link to the guest's Apple Wallet pass (opened as a normal link, so Safari offers to add it)
 * @returns {String}        -   Full URL
 */
export const getSouvenirApplePassUrl = async (pnr) => {
    const { path } = await callBackend(`/fly/souvenir/apple-pass-link${souvenirQuery(pnr)}`);
    return `${BACKEND_URL}${path}`;
};

/**
 * A link that adds the guest's pass to Google Wallet
 * @returns {Object}        -   { saveUrl }
 */
export const getSouvenirGooglePass = (pnr) => callBackend(`/fly/souvenir/google-pass${souvenirQuery(pnr)}`);

/**
 * Every card from Unclaimed Declarations, with the lie marked (guests who were there, once the reveal is finished)
 * @returns {Object}        -   { cards: [{ number, fullName, fromCity, colour, statements: [3], lie, mine, reports, fellFor, rightPassenger, rightLie }] }
 */
export const getDeclarations = (pnr) => callBackend(`/fly/souvenir/declarations${souvenirQuery(pnr)}`);

// ---------------- The night: counter (staff key), arrivals board (public), admin (admin key) ---------------- //

const withKey = (key) => ({ "X-Fly-Key": key });

/**
 * Everyone's status for the TV, newest arrivals first: { now, closed, landed, total, latest, rows: [{ fromCode, fromCity, colour, status, arrivedAt }] }
 */
export const getArrivals = () => call("/fly/arrivals");

/**
 * Every guest for the counter: { guests: [{ pnr, fullName, seat, fromCode, fromCity, colour, arrivedAt }], landed, total, closed }
 */
export const getCounterGuests = (key) => call("/fly/counter/guests", { headers: withKey(key) });

/**
 * Marks a guest as arrived, from a scanned QR or typed PNR
 * @returns {Object}            -   { guest, already (they'd already arrived), landed, total }
 */
export const markArrived = (key, code) => call("/fly/counter/arrive", { method: "POST", body: { code }, headers: withKey(key) });

/**
 * Undoes one check-in at the counter (only if it's still the same one)
 */
export const undoArrival = (key, pnr, arrivedAt) => call("/fly/counter/undo", { method: "POST", body: { pnr, arrivedAt }, headers: withKey(key) });

/**
 * Closes (everyone not here is DIVERTED) or reopens arrivals
 */
export const setArrivalsClosed = (key, closed) => call("/fly/counter/arrivals-closed", { method: "POST", body: { closed }, headers: withKey(key) });

/**
 * Every guest with their check-in, declarations and arrival: { closed, guests: [...] }
 */
export const getAdminGuests = (key) => call("/fly/admin/guests", { headers: withKey(key) });

/**
 * Sets or clears a guest's email
 */
export const setGuestEmail = (key, pnr, email) => call(`/fly/admin/guests/${encodeURIComponent(pnr)}`, { method: "PATCH", body: { email }, headers: withKey(key) });

/**
 * Clears every arrival and reopens arrivals (for testing before the party)
 */
export const resetArrivals = (key) => call("/fly/admin/reset-arrivals", { method: "POST", headers: withKey(key) });

/**
 * Sends an email from the host's Gmail, one copy per recipient
 * @param {Object} email        -   { to: [addresses], subject, text, attachments: [{ filename, content (base64), contentType }] }
 * @returns {Object}            -   { sent: [addresses], failed: [{ to, error }] }
 */
export const sendEmail = (key, email) => call("/sendEmail", { method: "POST", body: email, headers: withKey(key) });

/**
 * Every declaration as an anonymous card for the wall, numbered, statements mixed: { cards: [{ number, statements }] }.
 * No names and no lie: the host doesn't get to know either.
 */
export const getDeclarationCards = (key) => call("/fly/admin/declaration-cards", { headers: withKey(key) });

/**
 * Every voice message, anonymous and numbered: { memos: [{ number, id, recordedAt }] }
 */
export const getVoiceMemos = (key) => call("/fly/admin/voice-memos", { headers: withKey(key) });

/**
 * One voice message's audio, as a URL an <audio> element can play (revoke it when done)
 */
export const voiceMemoUrl = async (key, id) => {
    const response = await fetch(`${BACKEND_URL}${apiPath(`/fly/admin/voice-memos/${encodeURIComponent(id)}`)}`, { headers: withKey(key) });
    if (!response.ok) throw new BackendError(`Couldn't load the recording (${response.status})`, response.status);
    return URL.createObjectURL(await response.blob());
};

// ---------------- Unclaimed Declarations: the guessing game ---------------- //

/**
 * A guest's whole deck in one request: { phase, opensAt, closesAt, me: { pnr, firstName, card }, cards: [{ number, statements }],
 * passengers: [{ pnr, name, city, colour }], reports: { [number]: { suspectPnr, lie, updatedAt } }, pins: [numbers],
 * results (once the reveal is finished): { score, cards: { [number]: { passenger, lie, rightPassenger, rightLie } } } }
 */
export const getImmigration = (pnr) => call(`/fly/immigration/${encodeURIComponent(pnr)}`, { withCookies: true });

/**
 * Files or amends a report on a card
 */
export const fileReport = (pnr, number, suspectPnr, lie) => call(`/fly/immigration/${encodeURIComponent(pnr)}/reports/${number}`, { method: "PUT", body: { suspectPnr, lie }, withCookies: true });

/**
 * Pins or unpins a card
 */
export const setPin = (pnr, number, pinned) => call(`/fly/immigration/${encodeURIComponent(pnr)}/pins/${number}`, { method: pinned ? "PUT" : "DELETE", withCookies: true });

/**
 * The TV: { phase, opensAt, closesAt, reports, officers } while guessing, { phase, revealStarted, reveal } after
 */
export const getLeaderboard = () => call("/fly/leaderboard");

/**
 * The game for the host's admin page: { phase, opensAt, closesAt, revealStarted, cards, declared, players, reports, officers }
 */
export const getAdminGame = (key) => call("/fly/admin/game", { headers: withKey(key) });

/**
 * The host's remote (signed in with Google as the host): what the TV is showing, { phase, closesAt, reveal }
 */
export const getRemote = () => call("/fly/remote");

/**
 * The remote (or the TV, with the admin key) moves the TV's reveal to a step: { step, steps }
 */
export const setRemoteStep = (step, key) => call("/fly/remote/step", { method: "POST", body: { step }, headers: key ? withKey(key) : undefined });

/**
 * The TV's and the remote's controls: close (end the timer now) | extend (5 more minutes) | reveal-people (who declared
 * every card, on guests' phones only) | start (show the result on
 * the TV) | finish (guests see their scores). From the remote (signed in as the host), or the TV with the admin key.
 * @returns {Object}    -   What the TV shows: { phase, closesAt, revealStarted, reveal }
 */
export const hostAction = (action, key) => call(`/fly/remote/${action}`, { method: "POST", headers: key ? withKey(key) : undefined });

// ---------------- The trial run's tools (/fly2/admin only) ---------------- //

/**
 * Starts the trial again: the real guest list, nobody checked in, no game
 */
export const resetTrial = (key) => call("/fly/admin/trial/reset", { method: "POST", headers: withKey(key) });

/**
 * Starts the trial's game: the cards lock and guessing opens for 30 minutes: { started, minutes }
 */
export const startTrial = (key) => call("/fly/admin/trial/start", { method: "POST", headers: withKey(key) });

/**
 * Made-up declarations for every attending trial guest who hasn't declared: { declared }
 */
export const fakeTrialDeclarations = (key) => call("/fly/admin/trial/fake-declarations", { method: "POST", headers: withKey(key) });

/**
 * 5 random trial guests file a report on every card: { players }
 */
export const fakeTrialReports = (key) => call("/fly/admin/trial/fake-reports", { method: "POST", headers: withKey(key) });
